import * as React from 'react';
import { DownOutlined } from '@ant-design/icons';
import { ColorPicker, Dropdown, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import styles from '../../lexical-editor.module.css';

export function ToolbarColorButton({
  active = false,
  ariaLabel,
  color,
  defaultColor,
  disabled = false,
  icon,
  onChange,
  presets,
}: {
  active?: boolean;
  ariaLabel: string;
  color: string;
  defaultColor: string;
  disabled?: boolean;
  icon: React.ReactNode;
  onChange: (nextColor: string) => void;
  presets: NonNullable<React.ComponentProps<typeof ColorPicker>['presets']>;
}) {
  return (
    <ColorPicker
      allowClear
      disabled={disabled}
      onChange={(_, css) => {
        onChange(css);
      }}
      onClear={() => {
        onChange(defaultColor);
      }}
      placement="bottomLeft"
      presets={presets}
      showText={false}
      trigger="click"
      value={color}
    >
      <Tooltip mouseEnterDelay={0.4} title={ariaLabel}>
        <button
          aria-label={ariaLabel}
          className={styles.toolbarButton}
          data-active={active}
          disabled={disabled}
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          type="button"
        >
          <span className={styles.colorButtonContent}>
            {icon}
            <span
              className={styles.colorIndicator}
              style={{ backgroundColor: color }}
            />
          </span>
        </button>
      </Tooltip>
    </ColorPicker>
  );
}

export function ToolbarDropdownButton({
  active = false,
  disabled = false,
  icon,
  items,
  label,
  onClick,
}: {
  active?: boolean;
  disabled?: boolean;
  /** Shown instead of the label, which then becomes the tooltip and accessible name. */
  icon?: React.ReactNode;
  items: MenuProps['items'];
  label: string;
  onClick: (key: React.Key) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const popupRef = React.useRef<HTMLDivElement>(null);

  // antd's own outside-click detection misses clicks here and the portaled
  // menu does not follow scrolling containers, so close it ourselves.
  React.useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        popupRef.current?.contains(target)
      ) {
        return;
      }
      close();
    };
    const onScroll = (event: Event) => {
      if (popupRef.current?.contains(event.target as Node)) return;
      close();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('blur', close);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('blur', close);
    };
  }, [open]);

  return (
    <Dropdown
      menu={{
        items,
        onClick: ({ key }) => {
          setOpen(false);
          onClick(key);
        },
      }}
      onOpenChange={setOpen}
      open={open}
      placement="bottomLeft"
      popupRender={(menu) => <div ref={popupRef}>{menu}</div>}
      trigger={['click']}
    >
      <Tooltip mouseEnterDelay={0.4} title={icon ? label : undefined}>
        <button
          ref={triggerRef}
          aria-label={icon ? label : undefined}
          className={
            icon
              ? styles.toolbarIconDropdownButton
              : styles.toolbarDropdownButton
          }
          data-active={active}
          disabled={disabled}
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          type="button"
        >
          {icon ?? <span className={styles.toolbarDropdownLabel}>{label}</span>}
          <DownOutlined />
        </button>
      </Tooltip>
    </Dropdown>
  );
}

export function ToolbarButton({
  active = false,
  ariaLabel,
  disabled = false,
  icon,
  onClick,
}: {
  active?: boolean;
  ariaLabel: string;
  disabled?: boolean;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <Tooltip mouseEnterDelay={0.4} title={ariaLabel}>
      <button
        aria-label={ariaLabel}
        className={styles.toolbarButton}
        data-active={active}
        disabled={disabled}
        onMouseDown={(event) => {
          event.preventDefault();
        }}
        onClick={onClick}
        type="button"
      >
        {icon}
      </button>
    </Tooltip>
  );
}
